import { Flex, NumberInput, SimpleGrid, Text } from "@mantine/core";
import { SelectItem } from "@mantine/core/lib/Select/types";
import { notifications } from "@mantine/notifications";
import { OperationResult } from "@urql/core";
import map from "lodash/map";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";

import { useCreateDeskCollection } from "../../api/hooks/deskCollection/deskCollection/useCreateDeskCollection.ts";
import Table from "../../components/Table/Table.tsx";
import {
  DeskCollectionType,
  Exact,
  SendDeskCollectionInput,
  SendDeskCollectionMutation,
  UpdateDeskCollectionInput,
  UpdateDeskCollectionMutation,
} from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { DeskCollection } from "../../types/deskCollection/deskCollection.ts";
import { parseStringToNumber } from "../../utils/number.ts";
import { getPagesCount } from "../../utils/pagination.ts";
import { useDeskCollectionAutogroupData } from "./hooks/useDeskCollectionAutogroupData.tsx";
import { useDeskCollectionTableData } from "./hooks/useDeskCollectionTableData.tsx";

interface Props {
  templateOptions: SelectItem[];
  onInlineEdit: (
    id: number,
    value: UpdateDeskCollectionInput
  ) => Promise<
    OperationResult<
      UpdateDeskCollectionMutation,
      Exact<{
        id: number;
        input: UpdateDeskCollectionInput;
      }>
    >
  >;
  onSend: (
    id: number,
    message: string
  ) => Promise<
    OperationResult<
      SendDeskCollectionMutation,
      Exact<{
        id: number;
        input: SendDeskCollectionInput;
      }>
    >
  >;
}

const DeskCollectionAutogroup = ({
  templateOptions,
  onInlineEdit,
  onSend,
}: Props) => {
  const [page, setPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<DataTableSortStatus>({
    columnAccessor: "customerName",
    direction: "asc",
  });
  const [filter, setFilter] = useState<{
    lowestDpd: number | "";
    highestDpd: number | "";
  }>({
    lowestDpd: -14,
    highestDpd: 7,
  });

  const { fetching, error, data, total, refresh } =
    useDeskCollectionAutogroupData({
      filter,
      orderBy,
      page,
    });

  const pageCount = getPagesCount(total);

  const [, createDeskCollection] = useMutationNotificationWrapper(
    useCreateDeskCollection(),
    { success: { show: false } }
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleUpdate = useCallback(
    (item: DeskCollection, input: UpdateDeskCollectionInput) => {
      if (!item.id) {
        createDeskCollection({
          input: {
            customerId: item.customer.id,
            picName: item.picName,
            phone: item.phone,
            invoices: map(item.invoices, "id"),
            type: DeskCollectionType.Automatic,
            ...input,
          },
        }).then(refresh);
        return;
      }

      onInlineEdit(item.id, input).then((data) => {
        if (!data.error) {
          refresh();
        }
      });
    },
    [createDeskCollection, onInlineEdit, refresh]
  );

  const handleSend = useCallback(
    async (id: number, message: string) => {
      await onSend(id, message);
    },
    [onSend]
  );

  const handleFilterChange = useCallback(
    (key: "lowestDpd" | "highestDpd", value: number | "") => {
      if (value === "") {
        return;
      }
      setFilter((prevFilter) => ({
        ...prevFilter,
        [key]: value,
      }));
    },
    [setFilter]
  );

  const [columns, rows] = useDeskCollectionTableData({
    data,
    templates: templateOptions,
    options: {
      inlineEdit: {
        picName: true,
        phone: true,
      },
    },
    onInlineEdit: handleUpdate,
    onSend: handleSend,
  });

  if (error) {
    notifications.show({
      message: "Something went wrong while trying to fetch data.",
      variant: "error",
    });
  }

  return (
    <Flex direction="column" gap={20}>
      <SimpleGrid
        cols={6}
        breakpoints={[
          { maxWidth: "sm", cols: 1 },
          { maxWidth: "lg", cols: 2 },
        ]}
        spacing={20}
        verticalSpacing={8}
      >
        <NumberInput
          required
          label="Lowest DPD"
          placeholder="Lowest DPD"
          error={filter.lowestDpd === ""}
          size="m"
          precision={0}
          parser={parseStringToNumber}
          hideControls
          value={filter.lowestDpd}
          onChange={(value: number) => handleFilterChange("lowestDpd", value)}
        />
        <NumberInput
          required
          label="Highest DPD"
          placeholder="Highest DPD"
          error={filter.highestDpd === ""}
          size="m"
          precision={0}
          parser={parseStringToNumber}
          hideControls
          value={filter.highestDpd}
          onChange={(value: number) => handleFilterChange("highestDpd", value)}
        />
        <Flex direction="column" gap={4}>
          <Text># of Customer</Text>
          <Text size="lg">{total ?? 0}</Text>
        </Flex>
      </SimpleGrid>
      <Table
        columns={columns}
        rows={rows}
        verticalAlignment="top"
        loading={fetching}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        defaultSort={orderBy}
        onSortChange={setOrderBy}
      />
    </Flex>
  );
};

export default DeskCollectionAutogroup;
